import type { ColumnDef } from '@tanstack/react-table';
import { SLBPosition } from '@/types';
import { getColumnId } from '@/types/table.types';
import { formatCellValue } from './column-width';

export const applyInventorySearch = (
  items: SLBPosition[],
  searchQuery: string,
  allColumns: ColumnDef<SLBPosition, unknown>[]
): SLBPosition[] => {
  const trimmedQuery = searchQuery.trim();
  if (!trimmedQuery) return items;

  if (trimmedQuery.includes('+')) {
    const parts = trimmedQuery.split(/\s*\+\s*/).map((part) => part.trim()).filter((part) => part.length > 0);
    if (parts.length >= 2) {
      const symbolQuery = parts[0].toLowerCase();
      const counterpartyQuery = parts.slice(1).join(' ').trim().toLowerCase();

      if (symbolQuery && counterpartyQuery) {
        return items.filter((item) => {
          const symbolId = (item.symbolId || '').toLowerCase();
          const isin = (item.isin || '').toLowerCase();
          const sedol = (item.sedol || '').toLowerCase();

          const symbolMatch =
            (symbolId && symbolId.includes(symbolQuery)) ||
            (isin && isin.includes(symbolQuery)) ||
            (sedol && sedol.includes(symbolQuery));

          if (!symbolMatch) return false;

          const counterparty = (item.counterparty || '').toLowerCase();
          const counterpartyMatch = counterparty && counterparty.includes(counterpartyQuery);

          return counterpartyMatch;
        });
      }
      return items;
    }

    const query = trimmedQuery.toLowerCase();
    return items.filter((item) => {
      return Object.entries(item).some(([, value]) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  }

  const query = trimmedQuery.toLowerCase();
  const queryWithoutCommas = query.replace(/,/g, '');
  const queryHasSymbols = query.includes('$') || query.includes('€') || query.includes('%');

  return items.filter((item) => {
    const matchesRaw = Object.entries(item).some(([, value]) => {
      if (value === null || value === undefined) return false;
      const stringValue = String(value).toLowerCase();

      if (typeof value === 'string') {
        return stringValue.includes(query);
      }

      if (typeof value === 'number') {
        const stringValueWithoutCommas = stringValue.replace(/,/g, '');

        if (queryHasSymbols) {
          return stringValue.includes(query) || stringValueWithoutCommas.includes(queryWithoutCommas);
        }

        const stringValueWithoutSymbols = stringValueWithoutCommas.replace(/[$€%]/g, '');
        return (
          stringValue.includes(query) ||
          stringValueWithoutCommas.includes(queryWithoutCommas) ||
          stringValueWithoutSymbols.includes(queryWithoutCommas)
        );
      }

      return stringValue.includes(query);
    });

    if (matchesRaw) return true;

    if (queryHasSymbols) {
      return Object.entries(item).some(([key, value]) => {
        if (value === null || value === undefined) return false;
        if (typeof value !== 'number') return false;

        const column = allColumns.find((col) => getColumnId(col) === key);
        if (!column) return false;

        const formattedValue = formatCellValue(column, value).toLowerCase();
        const formattedValueWithoutCommas = formattedValue.replace(/,/g, '');

        return formattedValue.includes(query) || formattedValueWithoutCommas.includes(queryWithoutCommas);
      });
    }

    return false;
  });
};
